import { createApp } from 'vue';
import 'vue3-toastify/dist/index.css';
import App from './App.vue';
import './tailstyle.css';
import {
  ApplicationEvents,
  CaseConfiguration,
  CaseOpenedForEditing,
  CaseOpeningFailed, CaseServiceInterface,
  Contexts,
  CoreConfiguration,
  CurrentCaseServiceInterface,
  getContainer,
  getContext,
  getOwnershipForAllContainers,
  Language,
  NamedBindings,
  OpenCaseRequested,
  TranslationService,
  TypesOfActionHooks,
  ActionHookInterface,
  ContainerTypes,
  PathToXsl,
  TypesOfDisplayHooks,
  setupDandelion,
  CoreConfigurationInterface,
  DataSynchService,
  CaseStorageInterface,
  SchedulerService,
  addContext,
  Helpers,
  VariableChangeMotivationForGcpHookComponent,
  ListOfChangesForGcpHookComponent,
  OptionsForShowDifferences,
  MagicNumbers,
  CaseListService,
} from "@dandelion/core";
import router from "./router";
import AdministeredAmine from "./components/AdministeredAmine.vue";
import CerebralArteries from "./components/CerebralArteries.vue";
import MiddleCerebralArteries from "./components/middleCerebralArteries.vue";
import AnteriorStroke from "./components/AnteriorStroke.vue";
import PosteriorStroke from "./components/PosteriorStroke.vue";
import MinorRespiratory from "./components/MinorRespiratoryPlugin.vue";
import SelectSeatEarlyPostProcedure from "./components/SelectSeatEarlyPostProcedure.vue";
import { Messages } from "./enum/Messages";
import { NamedServices } from "./enum/NamedServices";
import CollapseHook from "./hooks/CollapseHook.vue";
import ColumnsHook from "./hooks/ColumnsHook.vue";
import ImporterHook from "./hooks/ImporterHook.vue";
import { ImporterService } from "./services/ImporterService";
import { UserIdleTimeManager } from "./utils/UserIdleTimeManager";
import { ReadmissionManagerRunner } from "./services/ReadmissionManagerRunner";
import { UserService } from "./services/UserService";
import defaultTranslations from "../public/defaultTranslations.json";
import { Users } from "./enum/Users";
import { servicesList, sharedServicesList } from "./services/ServicesList";
import { YearService } from "./services/YearService";
import ReadmissionHashChange from "./entities/ReadmissionHashChange";
import { ReadmissionStorageInterface } from "./interfaces/ReadmissionStorageInterface";
import { NamedStorages } from './enum/NamedStorages';
import { UserProvider } from './services/UserProvider';
import { RESTPreferenceStorage } from './repository/RESTPreferenceStorage';
import { Preferences } from './enum/Preferences';
import { LocalPreferencesStorage } from './repository/LocalPreferencesStorage';
import { defaultCentrePreferences, defaultUserPreferences } from './entities/DefaultUserPreferences';
import { PreferencesService } from './services/PreferencesService';
import AutoCompleteAntibiotici from './components/AutoCompleteAntibiotici.vue';
import AutoCompleteAntifungini from './components/AutoCompleteAntifungini.vue';
import SelectVolumeLesionTrauma from './components/SelectVolumeLesionTrauma.vue';
import SelectLesionDiffPostTrauma from './components/SelectLesionDiffPostTrauma.vue';
import SelectShiftTrauma from './components/SelectShiftTrauma.vue';
import SelectCisterneTrauma from './components/SelectCisterneTrauma.vue';
import ConsentChoice from './components/ConsentChoice.vue';
import { OVERRIDE_CrfToHTMLService } from './services/OVERRIDE_CrfToHtmlService';
import { buildMinorRespiratoryPeriods } from './utils/MinorRespiratoryUtils';
import { ImporterMode } from './enum/ImporterMode';
import { DataSynchListServiceForProsafe } from './services/DataSynchListServiceForProsafe';
import { CurrentPageServiceForTree } from './services/CurrentPageServiceForTree';
import CerebralArteriesNonBleed from './components/CerebralArteriesNonBleed.vue';
import VasiImaging from './components/VasiImaging.vue';
import CerebralArteriesStroke from './components/CerebralArteriesStroke.vue';
import _ from 'lodash';
import { ExportConfigurationsStorageForProsafe } from './repository/ExportConfigurationsStorageForProsafe';
import { ProsafeExportButtonConfigs } from './utils/ProsafeExportButtonConfigs';
import { ConsoleLogger } from '@dandelion/core';
import { useModalService } from './services/ModalService';

const app = createApp(App);
app.use(router);
const useLocalStorage = import.meta.env.VITE_APP_LOCAL_STORAGE && import.meta.env.VITE_APP_LOCAL_STORAGE == 'true';

const userProvider = new UserProvider();
userProvider.tryAuthenticate();
const userPreferencesStorage = useLocalStorage ? new LocalPreferencesStorage(Preferences.USERPREFERENCES, defaultUserPreferences) :
  new RESTPreferenceStorage('UsersData/', defaultUserPreferences, userProvider);
const centrePreferencesStorage = useLocalStorage ? new LocalPreferencesStorage(Preferences.CENTREPREFERENCES, defaultCentrePreferences) :
  new RESTPreferenceStorage('CentresData/', defaultCentrePreferences, userProvider);

const gcpStrategy = "never"; // TODO: riabilitare quando sono definite tutte le questioni intorno alle GCP: (await centrePreferencesStorage.getPreference(Preferences.GCPSTRATEGY))?.value as OptionsForShowDifferences;
const gcpIsActive = false; // TODO: riabilitare quando sono definite tutte le questioni intorno alle GCP: gcpStrategy === "always";
const afterVariableHooks = [CollapseHook];
const afterChangedVariableHooks = [ImporterHook]
if (gcpIsActive) {
  afterVariableHooks.push(ListOfChangesForGcpHookComponent)
  afterVariableHooks.push(VariableChangeMotivationForGcpHookComponent);
}


const variablesToBeCached: Array<string> = [
  'admissionKey',
  'icuAdmDate',
  'core.personalData.name',
  'core.personalData.surname',
  'core.personalData.birthDate',
  'core.personalData.sex',
  'core.administrativeInfo.icuAdmDate',
  'core.administrativeInfo.ehrIdAdmission',
  'consent',
  'consentNotNecessary',
];

// ignore ion-icons in the template compiler
app.config.compilerOptions.isCustomElement = (tag: any) => ['ion-icon'].includes(tag);
const isAdminOrWriter = async () => user?.value?.role === Users.ADMIN || user?.value?.role === Users.WRITER;
const isAdmin = async () => user?.value?.role === Users.ADMIN;

const createPolicy = (checkFn: (context: string) => Promise<boolean>) => ({
  run: async (context: string) => {
    if (await checkFn(context)) {
      return true;
    } else {
      logger.logError(ts.t(Messages.NO_RIGHTS_FOR_HOOK), {});
      return false;
    }
  },
});

const beforeCreatingHook: ActionHookInterface = {
  run: async (context: string) => {
    if (!isAdminOrWriter) {
      logger.logError(ts.t(Messages.NO_RIGHTS_FOR_HOOK), {});
      return false;
    }
    const newCurrentCaseService = getContainer(context, ContainerTypes.NEW_CASE).get(NamedBindings.CurrentCaseServiceForNewCase) as CurrentCaseServiceInterface;
    const askConsentValue = (await centrePreferencesService.getPreference(Preferences.ASKCONSENT)).value;
    const model = newCurrentCaseService.getModel();

    const previousModel = newCurrentCaseService.getPreviousModel();
    const cleanModel = JSON.parse(JSON.stringify(model));
    const cleanPreviousModel = JSON.parse(JSON.stringify(previousModel))
    const differences = findDifferences(cleanModel, cleanPreviousModel);

    updateLastSavingUser(model, differences, user.value?.username);

    const admissionDate = new Date(model.core.administrativeInfo.icuAdmDate).getFullYear();
    yearService.setAdmissionYear(admissionDate);
    model.admissionKey = `${newCurrentCaseService.getCurrentCase().value.substring(0, 15)}`;
    if (askConsentValue === 'false') {
      model.consentNotNecessary = true;
    }
    if (model.consent === 'no') {
      model.icuAdmDate = model?.core?.administrativeInfo?.icuAdmDate;
      //if consent is no, should not check readmission because we have no date except for icuAdmDate
      return true;
    }
    const readmissionStorage = getContainer().get("ReadmissionStorage") as ReadmissionStorageInterface;
    const readmissionManagerRunner = new ReadmissionManagerRunner(
      readmissionStorage,
      newCurrentCaseService.getCurrentCase().value ?? '',
      model
    );
    try {
      if (await readmissionManagerRunner.checkReadmissionsHypothesis()) {
        return await readmissionManagerRunner.waitForDialogToClose();
      }
      readmissionManagerRunner.unregisterRunner();
      return true;
    } catch (e: any) {
      logger.logError(ts.t(e), {});
      return false;
    }
  },
};

const beforeSavingHook: ActionHookInterface = {
  run: async (context: string) => {
    if (!isAdminOrWriter) {
      logger.logError(ts.t(Messages.NO_RIGHTS_FOR_HOOK), {});
      return false;
    }
    const currentCaseService = getContainer(context, ContainerTypes.DEFAULT).get(NamedBindings.CurrentCaseService) as CurrentCaseServiceInterface;
    const caseService = getContainer(context, ContainerTypes.DEFAULT).get(NamedBindings.CaseService) as CaseServiceInterface;
    const readmissionStorage = getContainer(context, ContainerTypes.DEFAULT).get("ReadmissionStorage") as ReadmissionStorageInterface;
    const { centreCode } = await caseService.getCaseMetaDataFor(currentCaseService.getCurrentCase().value);

    const model = currentCaseService.getModel();
    const previousModel = currentCaseService.getPreviousModel();

    if (coreConfiguration.showDifferencesOnSave !== "always" || !coreConfiguration.gcpIsActive) {
      const cleanModel = JSON.parse(JSON.stringify(model));
      const cleanPreviousModel = JSON.parse(JSON.stringify(previousModel))
      const differences = findDifferences(cleanModel, cleanPreviousModel);

      updateLastSavingUser(model, differences, user.value?.username);
    }

    const admissionDate = new Date(model.core?.administrativeInfo.icuAdmDate).getFullYear();
    yearService.setAdmissionYear(admissionDate);

    const readmissionHashChange = new ReadmissionHashChange(caseService, readmissionStorage)
    await readmissionHashChange.handleHashChangeIfNeeded(model, previousModel, currentCaseService.getCurrentCase().value, centreCode);
    return true;
  },
};

const beforeClosingHook: ActionHookInterface = {
  run: async (context) => {
    const ccs = getContainer(context).get(NamedBindings.CurrentCaseService) as CurrentCaseServiceInterface;

    if (ccs.isModelChanged.value) {
     const modal = useModalService();
      const ok = await modal.confirm("Hai dei dati non salvati, Sicuro di voler uscire SENZA salvare?");
      return ok; // must resolve true or false
    }

  
    return true;
  }
}

const beforePermanentlyDeletingHook: ActionHookInterface = {
  run: async (context, caseID: string) => {
    if (!isAdmin) {
      logger.logError(ts.t(Messages.NO_RIGHTS_FOR_HOOK), {});
      return false;
    }

    const caseService = getContainer(context).get(NamedBindings.CaseService) as CaseServiceInterface;
    const readmissionStorage = getContainer(context).get(NamedStorages.ReadmissionStorage) as ReadmissionStorageInterface;
    const readmissionHashChange = new ReadmissionHashChange(caseService, readmissionStorage);
    const { data, caseMetaData } = await caseService.getDataFor(caseID);
    await readmissionHashChange.removeCaseFromChains(caseID, data, caseMetaData.centreCode);
    
    return true;
  }
}

function findDifferences(obj1: any, obj2: any, parentPath = '', changes: Record<string, any> = {}) {
  for (const key in obj1) {
    const fullPath = parentPath ? `${parentPath}.${key}` : key;
    const prevValue = obj2[key] || null;
    const newValue = obj1[key];

    if (typeof obj1[key] === 'object' && obj1[key] !== null && !Array.isArray(obj1[key])) {
      if (obj2[key] && obj2[key].ACTIVE === false) {
        // Controlliamo se almeno una variabile ha un valore effettivo
        const hasValidValue = Object.values(obj1[key]).some(val =>
          val !== "" && val !== false && !(Array.isArray(val) && val.length === 0) && val !== null
        );

        if (!hasValidValue) continue;
      }

      findDifferences(obj1[key], obj2[key] || {}, fullPath, changes);
    } else {
      // Ignoriamo variabili vuote e `ACTIVE: false`
      if (newValue === "" || newValue === false || (Array.isArray(newValue) && newValue.length === 0) || newValue === null) continue;

      // Verifica che il valore sia effettivamente cambiato
      if (prevValue !== newValue) {
        changes[fullPath] = { oldValue: prevValue, newValue };
      }
    }
  }

  return changes;
}

function updateLastSavingUser(model: any, differences: Record<string, any>, currentUser: string | undefined) {
  const key = Symbol.for('LastSavingUser');
  if (!model[key]) {
    model[key] = {}; // Inizializziamo l'oggetto
  }

  for (const fullPath in differences) {
    let savedRecordArray: Array<any> = Helpers.getObjByPath(fullPath, model[key]) || [];

    // Se l'array è vuoto o non contiene già lo user, aggiungiamolo
    if (!savedRecordArray.some(record => record.user === currentUser)) {
      savedRecordArray.unshift({ user: currentUser });
    }

    Helpers.setObjByPath(fullPath, model[key], savedRecordArray);
  }
}

const caseConfiguration = new CaseConfiguration({
  caseName: 'Admission',
  mainCrf: { crfName: 'core' },
  childrenCRFs: [
    { crfName: 'colon' },
    { crfName: 'infections' },
    { crfName: 'esa' },
    { crfName: 'stroke' },
    { crfName: 'start' },
    { crfName: 'livertransplant' },
    { crfName: 'cch' },
    { crfName: 'trauma' }
  ],
  referenceDateVariableName: 'core.administrativeInfo.icuAdmDate',
  cachedVariables: variablesToBeCached,
});

/*console.log(import.meta.env.BASE_URL + 'xslt/');
console.log(import.meta.env.BASE_URL + 'xslt-overrides/');
console.log(import.meta.env.VITE_APP_API_BASE_URL);*/

await setupDandelion({
  coreConfiguration: new CoreConfiguration({
    accessLevelAndCrfConfigurations: [
      { accessLevel: "*", crfName: "core" },
      { accessLevel: "*", crfName: "infections" },
      { accessLevel: "*", crfName: "esa" },
      { accessLevel: "*", crfName: "start" },
      { accessLevel: "*", crfName: "cch" },
      { accessLevel: "*", crfName: "colon" },
      { accessLevel: "*", crfName: "livertransplant" },
      { accessLevel: "*", crfName: "start" }
    ],
    debug: (await userPreferencesStorage.getPreference(Preferences.DEBUG))!.value == 'true',
    statisticianMode: (await userPreferencesStorage.getPreference(Preferences.STATISTICIAN))!.value == 'true',
    defaultLanguage: (await userPreferencesStorage.getPreference(Preferences.LANGUAGES))!.value as Language,
    additionalTranslations: defaultTranslations,
    baseUrlForXslt: import.meta.env.BASE_URL + 'xslt/',
    baseUrlForOverrides: import.meta.env.BASE_URL + 'xslt-overrides/',
    baseUrlForKeys: (import.meta.env.VITE_APP_API_BASE_URL_FOR_KEYS ?? import.meta.env.VITE_APP_API_BASE_URL) + 'Keys/',
    baseUrlForCases: import.meta.env.VITE_APP_API_BASE_URL + 'Rows/',
    baseUrlForAccessRights: import.meta.env.VITE_APP_API_BASE_URL + 'AccessRights/',
    listOfOverridesSchemas: [
      PathToXsl.SCHEMA_TO_TREE,
      PathToXsl.SCHEMA_TO_DIFFS,
      PathToXsl.SCHEMA_TO_COLLECTION_PAGE,
    ],
    listOfCaseTypes: [caseConfiguration],
    encrypt: true,
    gcpIsActive,
    showDifferencesOnSave: gcpStrategy ?? 'never',
    marginForScroll: [10, 0],

    actionHooks: new Map<TypesOfActionHooks, ActionHookInterface>()
      .set(TypesOfActionHooks.BeforeCreating, beforeCreatingHook)
      .set(TypesOfActionHooks.BeforeSaving, beforeSavingHook)
      .set(TypesOfActionHooks.BeforeClosing, beforeClosingHook)
      .set(TypesOfActionHooks.BeforeNewCaseRequesting, createPolicy(isAdminOrWriter))
      .set(TypesOfActionHooks.BeforeDeleting, createPolicy(isAdminOrWriter))
      .set(TypesOfActionHooks.BeforePermanentlyDeleting, beforePermanentlyDeletingHook),
    pluggableDataTypes: {
      administeredAmine: AdministeredAmine,
      cerebralArteries: CerebralArteries,
      anteriorStroke: AnteriorStroke,
      vasiImaging: VasiImaging,
      middleCerebralArteries: MiddleCerebralArteries,
      posteriorStroke: PosteriorStroke,
      minorRespiratory: MinorRespiratory,
      selectSeatEarlyPostProcedure: SelectSeatEarlyPostProcedure,
      autoCompleteAntibiotici: AutoCompleteAntibiotici,
      autoCompleteAntifungini: AutoCompleteAntifungini,
      selectVolumeLesionTrauma: SelectVolumeLesionTrauma,
      selectLesionDiffPostTrauma: SelectLesionDiffPostTrauma,
      selectShiftTrauma: SelectShiftTrauma,
      selectCisterneTrauma: SelectCisterneTrauma,
      consentChoice: ConsentChoice,
      cerebralArteriesNonBleed: CerebralArteriesNonBleed,
      cerebralArteriesStroke: CerebralArteriesStroke
    },
    displayHooks: new Map()
      .set(TypesOfDisplayHooks.AfterVariable, afterVariableHooks)
      .set(TypesOfDisplayHooks.BeforeVariable, [ColumnsHook])
      .set(TypesOfDisplayHooks.AfterChangedVariable, afterChangedVariableHooks),
  }),
  sharedServices: [...sharedServicesList, { name: NamedStorages.UserProvider, instance: () => userProvider }],
  services: servicesList,
});

const container = getContainer(Contexts.DEFAULT);
const sharedContainer = getContainer(Contexts.SHARED);
const coreConfiguration = container.get(NamedBindings.CoreConfiguration) as CoreConfigurationInterface;
const importerService = container.get(NamedServices.ImporterService) as ImporterService;
const ts = container.get(NamedBindings.TranslationService) as TranslationService;
const userService = sharedContainer.get(NamedBindings.UserService) as UserService;
const yearService = getContainer().get(NamedServices.YearService) as YearService;
sharedContainer.rebind(NamedBindings.CRFToHTMLService).to(OVERRIDE_CrfToHTMLService).inSingletonScope();
sharedContainer.bind(NamedStorages.UserPreferenceStorage).toDynamicValue(() => userPreferencesStorage).inSingletonScope();
sharedContainer.bind(NamedStorages.CentrePreferenceStorage).toDynamicValue(() => centrePreferencesStorage).inSingletonScope();
sharedContainer.bind(NamedServices.UserPreferencesService).toDynamicValue(() =>
  new PreferencesService(userService, ts, sharedContainer.get(NamedStorages.UserPreferenceStorage))).inSingletonScope();
sharedContainer.bind(NamedServices.CentrePreferencesService).toDynamicValue(() =>
  new PreferencesService(userService, ts, sharedContainer.get(NamedStorages.CentrePreferenceStorage))).inSingletonScope();
sharedContainer.rebind(NamedBindings.ExportConfigurationsStorage).toDynamicValue(() =>
  new ExportConfigurationsStorageForProsafe(ProsafeExportButtonConfigs, sharedContainer.get(NamedStorages.CentrePreferenceStorage))).inSingletonScope();
const logger = container.get(NamedBindings.Logger) as ConsoleLogger;

sharedContainer.bind('buildMinorRespiratoryPeriods').toDynamicValue(() => buildMinorRespiratoryPeriods); //Allow to use the function in formulas, chnge when #1027 in dandelion Core is released

const user = userService.getCurrentUser();
await (sharedContainer.get(NamedServices.UserPreferencesService) as PreferencesService).init();
const centrePreferencesService = sharedContainer.get(NamedServices.CentrePreferencesService) as PreferencesService;
await centrePreferencesService.init();

// container.rebind(NamedBindings.CurrentPageService).toDynamicValue(() =>
//   new CurrentPageService(coreConfiguration, logger)).inSingletonScope();
container.rebind(NamedBindings.CurrentPageService).toDynamicValue(() =>
  new CurrentPageServiceForTree(coreConfiguration, logger)).inSingletonScope();

getContext(Contexts.DEFAULT).addEventListener(ApplicationEvents.OpenCaseRequested, (_, event) => {
  const caseID = (event as OpenCaseRequested).caseID;
  const currentCaseService = getContainer(Contexts.DEFAULT).get(NamedBindings.CurrentCaseService) as CurrentCaseServiceInterface;
  currentCaseService.open(caseID);
});

getContext(Contexts.DEFAULT).addEventListener(
  ApplicationEvents.CaseOpenedForEditing,
  async (_m: string, e: Event) => {
    const event: CaseOpenedForEditing = e as CaseOpenedForEditing;
    const caseStorage = container.get(NamedBindings.CaseStorage) as CaseStorageInterface;
    const importerService = container.get(NamedServices.ImporterService) as ImporterService;
    await caseStorage.lock(event.caseID);
    if (!importerService.isImporting.value) {
      //TODO - Rimuovere la seconda parte se si sposta importer in contesto separato
      await router.push(`/patient/${event.caseID}`);
    }
  }
);

getContext(Contexts.DEFAULT).addEventListener(
  ApplicationEvents.CaseOpeningFailed,
  async (_m: string, e: Event) => {
    const event: CaseOpeningFailed = e as CaseOpeningFailed;
    await router.push(`/`);
    logger.logError(ts.t(Messages.CASE_OPENING_FAILED) + (event.caseID?.substring(0, 14) ?? ''), {});
  }
);

const inactivityTimer = new UserIdleTimeManager(userService, ts, logger);
inactivityTimer.setupSessionExpiringTimer();

if ((await centrePreferencesService.getPreference(Preferences.TYPEIMPORT))?.value == ImporterMode.AUTOMATIC) {
  setInterval(() => importerService.run(), 1000 * 60 * 2);
}

getOwnershipForAllContainers();

addContext('contextForSynch');

const caseListService = getContainer('contextForSynch').get(NamedBindings.CaseListService) as CaseListService;
caseListService.endList.value = Number.MAX_SAFE_INTEGER;
/* Enable again if it's necessary to filter patients for synchronization
caseListService.setFilters([new FilterOnlyPatientsWithConsent()])
*/
getContainer('contextForSynch').rebind(NamedBindings.DataSynchListService).to(DataSynchListServiceForProsafe)
getContainer('contextForSynch').rebind(NamedBindings.DataSynchService).to(DataSynchService);
const synchService = getContainer('contextForSynch').get(NamedBindings.DataSynchService) as DataSynchService;
const scheduler = getContainer().get(NamedBindings.Scheduler) as SchedulerService;
synchService.startSynchCycle(10); //60 seconds
if (userService.getCurrentUser().value?.role === Users.ADMIN || userService.getCurrentUser().value?.role === Users.WRITER) {
  scheduler.startPeriodicReevaluation(MagicNumbers.periodicallyRunReevaluationMinutes); //5 minutes
}
app.mount('#app');